import {
  useState,
  useEffect,
  useCallback,
} from 'react';
import { OUTPUT_TTL } from '../../../../constants/Pac';
import {
  LockableButtonGroupProps,
  LockableButtonGroupState,
  DEFAULT_BUTTON_STATE,
  ButtonName,
} from './LockableButtonGroup';
import { useStores } from '../../../../hooks/use-stores';

export interface LockableButtonProps extends LockableButtonGroupProps {
  id: string;
}

const REQUIRE_BUTTON_RELEASE_BEFORE_NEXT_PRESS = true;

export const useLockableButtons = (props: LockableButtonProps) => {
  const { batchStore, lockableButtonStore } = useStores();
  const { upButtonPort, downButtonPort, id, upLimitReached, downLimitReached } = props;

  // Get initial state from the store
  const [state, setState] = useState<LockableButtonGroupState>(
    lockableButtonStore.getButtonState(id),
  );

  const resetOutput = useCallback((port?: string) => {
    if (port) {
      batchStore?.setOutput(port, '0');
    }
  }, [batchStore]);

  // Update store when state changes
  useEffect(() => {
    lockableButtonStore.setButtonState(id, state);
  }, [state, id, lockableButtonStore]);

  // Handle limits reached
  useEffect(() => {
    if (upLimitReached && state.buttonStates.up.active) {
      // If up limit reached and button is active, deactivate it
      setState(prevState => ({
        ...prevState,
        activeButton: prevState.activeButton === 'up' ? null : prevState.activeButton,
        buttonStates: {
          ...prevState.buttonStates,
          up: { ...DEFAULT_BUTTON_STATE },
        },
      }));
      resetOutput(upButtonPort);
    }

    if (downLimitReached && state.buttonStates.down.active) {
      // If down limit reached and button is active, deactivate it
      setState(prevState => ({
        ...prevState,
        activeButton: prevState.activeButton === 'down' ? null : prevState.activeButton,
        buttonStates: {
          ...prevState.buttonStates,
          down: { ...DEFAULT_BUTTON_STATE },
        },
      }));
      resetOutput(downButtonPort);
    }
  }, [upLimitReached, downLimitReached, state.buttonStates.up.active, state.buttonStates.down.active, resetOutput, upButtonPort, downButtonPort]);

  const setOutput = (port?: string) => {
    if (port) {
      batchStore?.setOutput(port, OUTPUT_TTL);
    }
  };

  const toggleLockMode = (value: boolean): void => {
    setState(prevState => {
      // Release any locked buttons when disabling lock mode
      if (!value) {
        // Reset outputs only for buttons that were active
        if (upButtonPort && prevState.buttonStates.up.active) resetOutput(upButtonPort);
        if (downButtonPort && prevState.buttonStates.down.active) resetOutput(downButtonPort);

        return {
          ...prevState,
          lockModeEnabled: value,
          holdButtonPressed: false,
          activeButton: null,
          buttonStates: {
            up: { ...DEFAULT_BUTTON_STATE },
            down: { ...DEFAULT_BUTTON_STATE },
          },
        };
      }

      return { ...prevState, lockModeEnabled: value };
    });
  };

  const handleButtonPress = (buttonName: ButtonName): void => {
    const { buttonStates, lockModeEnabled } = state;
    const currentState = buttonStates[buttonName];
    const otherButton = buttonName === 'up' ? 'down' : 'up';
    const currentPort = buttonName === 'up' ? upButtonPort : downButtonPort;
    const otherPort = buttonName === 'up' ? downButtonPort : upButtonPort;

    // Check if limit is reached for the button being pressed
    const limitReached = buttonName === 'up' ? upLimitReached : downLimitReached;
    if (limitReached) {
      // If limit is already reached, don't activate the button
      return;
    }

    if (
      REQUIRE_BUTTON_RELEASE_BEFORE_NEXT_PRESS &&
      buttonStates[otherButton].active
    ) {
      // If the other button is active, release it first and don't activate the new one yet
      setState(prevState => ({
        ...prevState,
        activeButton: null,
        buttonStates: {
          ...prevState.buttonStates,
          [otherButton]: { ...DEFAULT_BUTTON_STATE },
        },
      }));
      resetOutput(otherPort);
      return;
    }

    if (lockModeEnabled) {
      if (currentState.locked) {
        // If already locked, release it
        setState(prevState => ({
          ...prevState,
          activeButton: null,
          buttonStates: {
            ...prevState.buttonStates,
            [buttonName]: { ...DEFAULT_BUTTON_STATE },
          },
        }));
        resetOutput(currentPort);
      } else {
        // Lock this button and release the other only if it was active
        setState(prevState => ({
          ...prevState,
          activeButton: buttonName,
          buttonStates: {
            ...prevState.buttonStates,
            [buttonName]: { active: true, locked: true, wasLocked: false },
            [otherButton]: { ...DEFAULT_BUTTON_STATE },
          },
        }));
        setOutput(currentPort);
        if (buttonStates[otherButton].active) {
          resetOutput(otherPort);
        }
      }
    } else {
      // Regular press behavior
      setState(prevState => ({
        ...prevState,
        buttonStates: {
          ...prevState.buttonStates,
          [buttonName]: { ...currentState, active: true },
        },
      }));
      setOutput(currentPort);
    }
  };

  const handleButtonRelease = (buttonName: ButtonName): void => {
    const { buttonStates, lockModeEnabled } = state;
    const currentState = buttonStates[buttonName];
    const currentPort = buttonName === 'up' ? upButtonPort : downButtonPort;

    if (!lockModeEnabled) {
      setState(prevState => ({
        ...prevState,
        buttonStates: {
          ...prevState.buttonStates,
          [buttonName]: { ...currentState, active: false },
        },
      }));
      resetOutput(currentPort);
    }
  };

  const handleHoldPress = (): void => {
    const { buttonStates, activeButton } = state;

    if (!activeButton) return;

    const currentPort = activeButton === 'up' ? upButtonPort : downButtonPort;

    setState(prevState => ({
      ...prevState,
      holdButtonPressed: true,
      buttonStates: {
        ...prevState.buttonStates,
        [activeButton]: { ...buttonStates[activeButton], active: false, wasLocked: true },
      },
    }));

    resetOutput(currentPort);
  };

  const handleHoldRelease = (): void => {
    const { buttonStates, activeButton } = state;

    if (!activeButton) return;

    const currentPort = activeButton === 'up' ? upButtonPort : downButtonPort;

    // Check if limit is reached for the active button
    const limitReached = activeButton === 'up' ? upLimitReached : downLimitReached;
    if (limitReached) {
      // If limit is reached, don't reactivate the button
      setState(prevState => ({
        ...prevState,
        holdButtonPressed: false,
        activeButton: null,
        buttonStates: {
          ...prevState.buttonStates,
          [activeButton]: { ...DEFAULT_BUTTON_STATE },
        },
      }));
      return;
    }

    setState(prevState => ({
      ...prevState,
      holdButtonPressed: false,
      buttonStates: {
        ...prevState.buttonStates,
        [activeButton]: { ...buttonStates[activeButton], active: true, wasLocked: false },
      },
    }));

    setOutput(currentPort);
  };

  return {
    state,
    toggleLockMode,
    handleButtonPress,
    handleButtonRelease,
    handleHoldPress,
    handleHoldRelease,
  };
};
